import { useSelector } from "react-redux";
import { SIDEMENU } from "../databucket";
import {
  faUsers,
  faUserTie,
  faChartLine,
  faTasks,
  faCog,
  faChartBar,
  faEnvelope,
  faDatabase,
  faTachometerAlt,
  faMailBulk,
  faWallet,
  faChartSimple,
  faFileInvoiceDollar,
  faRoute,
  faExclamationTriangle,
  faPhone,
  faUserPlus,
  faLaptop,
  faFolder,
  faNoteSticky,
  faPenToSquare,
  faHardDrive,
  faTrash,
  faGlobe,
  faTableCells,
  faComments,
} from "@fortawesome/free-solid-svg-icons";

/**
 * sideMenu — single source of truth for turning the SIDEMENU + the user's
 * route access into the Webtop's desktop icons / folders (and, later, the
 * search index in Phase 6).
 *
 * The access-filter logic is copied VERBATIM from views/layout/Sidebar.jsx
 * (`filterSideMenuByRoutes`) so the desktop shows exactly what the sidebar
 * would — permissions stay the single spine (WEBTOP_SPEC §2). If the sidebar's
 * filter ever changes, mirror it here.
 */

// Routes accessible to all logged-in users without access management.
export const ALWAYS_VISIBLE_ROUTES = ["/MyDashboard"];

/**
 * Built-in Webtop shell apps — not backend routes, so they live OUTSIDE the
 * permission-driven SIDEMENU. They appear on the desktop and in Start search for
 * everyone. Each opens EITHER a slice `action` (e.g. the Sticky Notes panel) OR
 * a webtop window via `openPath` (e.g. Notepad). DesktopIcons / StartMenu handle
 * both.
 */
// The embedded browser is an Electron <webview> feature — Desktop only. On the
// web app there's no workspace-browser route, so only expose it under Electron
// (keeps this file identical across both apps per Rule 1).
const IS_ELECTRON = typeof window !== "undefined" && !!window.electronAPI;

export const WEBTOP_SHELL_APPS = [
  { key: "this-pc", name: "This PC", icon: faHardDrive, openPath: "/webtop-files" },
  { key: "recycle-bin", name: "Recycle Bin", icon: faTrash, openPath: "/webtop-recycle-bin" },
  { key: "sticky-notes", name: "Sticky Notes", icon: faNoteSticky, action: "openNotesApp" },
  { key: "notepad", name: "Notepad", icon: faPenToSquare, openPath: "/webtop-notepad" },
  { key: "sheets", name: "Sheets", icon: faTableCells, openPath: "/webtop-sheets" },
  // In the legacy app the browser opened from the Header; the webtop replaced
  // that, so expose it as a shell app. Route lives in superAdminRoutes
  // (workspace-browser → EmbeddedBrowser).
  ...(IS_ELECTRON
    ? [{ key: "browser", name: "Browser", icon: faGlobe, openPath: "/workspace-browser" }]
    : []),
];

// Normalize a subfield's route the same way the sidebar does.
export const subRouteOf = (sub) =>
  sub.route || (sub.name?.startsWith("/") ? sub.name : `/${sub.name}`);

export const filterSideMenuByRoutes = (menu, currentUserRoute) => {
  if (!Array.isArray(currentUserRoute) || !currentUserRoute.length) return [];

  return menu
    .map((section) => {
      const filteredSubfields = section.Subfields.filter((sub) => {
        const subRoute = subRouteOf(sub);
        if (ALWAYS_VISIBLE_ROUTES.includes(subRoute)) return true;
        return currentUserRoute.some(
          (userRoute) => userRoute === subRoute || userRoute.startsWith(subRoute + "/")
        );
      });
      if (!filteredSubfields.length) return null;
      return { ...section, Subfields: filteredSubfields };
    })
    .filter(Boolean);
};

/**
 * canAccessPath — does the current user still have access to a deep-link path
 * (e.g. a pinned record shortcut "/sales/123")? True if any of their access
 * routes is the path or a parent of it. When access isn't loaded yet (empty
 * routes), returns true so we never falsely block (can't verify).
 */
export const canAccessPath = (routes, path) => {
  if (!Array.isArray(routes) || routes.length === 0) return true;
  const p = String(path || "").startsWith("/") ? path : `/${path}`;
  return routes.some((r) => {
    const rr = String(r || "").startsWith("/") ? r : `/${r}`;
    return p === rr || p.startsWith(`${rr}/`);
  });
};

// FontAwesome icon per top-level menu, matching the sidebar's iconMap.
const ICON_MAP = {
  Employees: faUsers,
  Clients: faUserTie,
  Sales: faChartLine,
  Requests: faTasks,
  CROptions: faCog,
  Time_Records: faChartBar,
  Email: faEnvelope,
  Data_Bucket: faDatabase,
  Dashboard: faTachometerAlt,
  Bulk_Emails: faMailBulk,
  Finance: faWallet,
  Outlook: faEnvelope,
  Reports: faChartSimple,
  Generate_New_PaySlip: faFileInvoiceDollar,
  Dynamic_Route: faRoute,
  Admin_Issues: faExclamationTriangle,
  User_Issues: faExclamationTriangle,
  Email_Validation: faEnvelope,
  Ring_Central: faPhone,
  Hiring: faUserPlus,
  Workspace: faLaptop,
  WhatsApp: faComments,
};

export const getMenuIcon = (name) => ICON_MAP[String(name || "").replace(/ /g, "_")] || faFolder;

/**
 * Flatten the (filtered) menu into a list of launchable leaf apps —
 * `{ key, title, section, path }`. Used by the Phase 6 search index.
 */
export const flattenMenu = (menu) =>
  menu.flatMap((section) =>
    section.Subfields.map((sub) => ({
      key: `${section.name}:${sub.subId}`,
      title: sub.displayName,
      section: section.name,
      path: subRouteOf(sub),
    }))
  );

/**
 * Hook returning the access-filtered SIDEMENU for the current user.
 *
 * @param {object}  opts
 * @param {boolean} opts.devFallback - When the user has no loaded route access
 *   (e.g. viewing the standalone /webtop preview without going through the
 *   authed shell), return the FULL menu so the surface is still demoable.
 *   Real integration passes devFallback=false so access strictly applies.
 */
export const useFilteredSideMenu = ({ devFallback = false } = {}) => {
  const routes = useSelector((state) => state?.employee?.currentEmployeeAccess?.routes) || [];
  const filtered = filterSideMenuByRoutes(SIDEMENU, routes);
  if (filtered.length === 0 && devFallback) return SIDEMENU;
  return filtered;
};
